import React, {
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import geojsonExtent from '@mapbox/geojson-extent';
import './Mapbox.sass';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import each from 'lodash/each';
import {
  convertPolygonToGEOJSONFormat,
  convertPointToGEOJSONFormat,
  createSourceConfig,
  createGeometryConfig,
  createFeatureConfig,
  createPolyLayerConfig,
  createPlaceLayerConfig,
  createPointLayerConfig,
  getCoordinates,
  createDropoffLayerConfig,
  createDropoffSourceConfig,
  getSelectedDropoffCoordinates
} from '../../utils/mapUtils'

const DEFAULT_STYLE = 'mapbox://styles/mapbox/streets-v9';
const DEFAULT_CENTER = new mapboxgl.LngLat(-86.163199, 39.7679346);


const Mapbox =
  ({
    deliveryAreas = [],
    pickupLocations = [],
    kitchenInZone = true,
    width = '100%',
    height = '100%',
    zoom = 16,
    mapStyle = DEFAULT_STYLE,
    showDeliveryAreas = false,
    showDriverMarker = false,
    showDestMarker = true,
    showNavControls = false,
    showPickupLocations = false,
    location = {},
    driverLocation = {},
    kitchenLocation = {},
    destinationMarkerClassName = 'mapbox-destination-marker',
    showPlaces = false,
    currentPlace,
    selectedDropoff,
    onPickupLocationClicked,
    currentPickupLocationId,
  }) => {
    const [initialized, setInitialized] = useState(false);
    const [animate, setAnimate] = useState(false);
    const [renderedDestCoordinates, setRenderedDestCoordinates] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [styleLoaded, setStyleLoaded] = useState(false);
    const [mapUnsupported, setMapUnsupported] = useState(false);

    const mapRef = useRef(null);
    const destMarkerRef = useRef(null);
    const driverMarkerRef = useRef(null);
    const geoExtentRef = useRef(null);
    const map = useRef(null);
    const destMarker = useRef(null);
    const driverMarker = useRef(null);
    const deliveryAreasRef = useRef([]);
    const currentPlaceRef = useRef(null);
    const dropoffPointsRef = useRef(null);
    const pickupLocationsRef = useRef({});

    const destCoordinates = getCoordinates(location);
    const driverCoordinates = getCoordinates(driverLocation);
    const defaultCoordinates = getCoordinates(kitchenLocation);

    const resizeMap = useCallback(
      () => {
        if (map.current) {
          map.current.resize();
        }
      },
      [ map ]
    );

    const fitMapToBounds = useCallback(
      () => {
        let geometry = undefined;
        let allFeatures = [];
        const inZone = !isEmpty(location) ? kitchenInZone : false;

        if (showDestMarker && !isEmpty(location)) {
          geometry = createGeometryConfig("Point", destCoordinates);
          allFeatures = [ createFeatureConfig(geometry) ];
        }

        if (showDriverMarker) {
          geometry = createGeometryConfig("Point", driverCoordinates);
          allFeatures = [ ...allFeatures, createFeatureConfig(geometry) ];
        }

        if (showPickupLocations && !isEmpty(pickupLocations)) {
          each(pickupLocations, (pickupLocation, i) => {
            const coordinates = convertPointToGEOJSONFormat([get(pickupLocation, 'pickup_details.location', {})])
            geometry = createGeometryConfig("Point", coordinates);

            if (geometry) {
              allFeatures = [ ...allFeatures, createFeatureConfig(geometry) ];
            }
          })
        }

        if (showPlaces && !isEmpty(currentPlace)) {
          const polygon = get(currentPlace, 'polygon');
          const dropoffPoints = get(currentPlace, 'dropoff_locations', [])
            .map(({point}) => createFeatureConfig(point));
          const name = get(currentPlace, 'id', '').replace(/[^A-Z0-9]/ig, '').toLowerCase();
          const polygonSourceId =`source_place_polygon_${name}`;
          const pointsSourceId =`source_place_points_${name}`;

          each(deliveryAreasRef.current, id => map.current.setLayoutProperty(id, 'visibility', 'none'));

          if ( map.current.isStyleLoaded() && !map.current.getSource(polygonSourceId) ) {
            map.current.addSource(polygonSourceId, createSourceConfig(polygon));
            map.current.addLayer(createPlaceLayerConfig(polygonSourceId));
            currentPlaceRef.current = polygonSourceId;

            map.current.addSource(pointsSourceId, createDropoffSourceConfig(dropoffPoints));
            map.current.addLayer(createDropoffLayerConfig(pointsSourceId));
            dropoffPointsRef.current = pointsSourceId;

          } else if (map.current.getSource(polygonSourceId)) {
            map.current.setLayoutProperty(polygonSourceId, 'visibility', 'visible');
            map.current.setLayoutProperty(pointsSourceId, 'visibility', 'visible');
          }

          if (polygon) {
            allFeatures = [
              ...allFeatures,
              createFeatureConfig(polygon),
              ...dropoffPoints
            ];
          }

        } else if (!inZone && showDeliveryAreas) {
          if (!isNil(currentPlaceRef.current)) {
            map.current.setLayoutProperty(currentPlaceRef.current, 'visibility', 'none');
            map.current.setLayoutProperty(dropoffPointsRef.current, 'visibility', 'none');
          }

          deliveryAreas.map((deliveryArea, i) => {
            if (deliveryArea.type === 'polygon')  {
              const coordinates = convertPolygonToGEOJSONFormat(deliveryArea.coordinates);
              geometry = createGeometryConfig("Polygon", coordinates);
              const name = get(deliveryArea, 'name', '').replace(/[^A-Z0-9]/ig, '').toLowerCase();
              const sourceId =`source_polygon_${name}`;

              if ( map.current.isStyleLoaded() && !map.current.getSource(sourceId) ) {
                map.current.addSource(sourceId, createSourceConfig(geometry));
                map.current.addLayer(createPolyLayerConfig(sourceId));
                deliveryAreasRef.current = [ ...deliveryAreasRef.current, sourceId ];
              } else if (map.current.getSource(sourceId)) {
                map.current.setLayoutProperty(sourceId, 'visibility', 'visible');
              }
            }

            if (deliveryArea.type === 'point') {
              const coordinates = convertPointToGEOJSONFormat(deliveryArea.coordinates);
              const meters = get(deliveryArea, 'buffer', 0);
              const lat = get(deliveryArea, 'coordinates[0].lat');
              geometry = createGeometryConfig("Point", coordinates);
              const name = get(deliveryArea, 'name', '').replace(/[^A-Z0-9]/ig, '').toLowerCase();
              const sourceId =`source_circle_${name}`;

              if ( map.current.isStyleLoaded() && !map.current.getSource(sourceId) ) {
                map.current.addSource(sourceId, createSourceConfig(geometry));
                map.current.addLayer(createPointLayerConfig(sourceId, meters, lat));
                deliveryAreasRef.current = [ ...deliveryAreasRef.current, sourceId ];
              } else if (map.current.getSource(sourceId)) {
                map.current.setLayoutProperty(sourceId, 'visibility', 'visible');
              }
            }
            if (geometry) {
              allFeatures = [ ...allFeatures, createFeatureConfig(geometry) ];
            }
            return undefined;
          });
        } else if (isEmpty(currentPlace)) {
          if (!isNil(currentPlaceRef.current)) {
            map.current.setLayoutProperty(currentPlaceRef.current, 'visibility', 'none');
            map.current.setLayoutProperty(dropoffPointsRef.current, 'visibility', 'none');
          }
        }

        if (allFeatures.length > 1 || (!inZone && showDeliveryAreas && allFeatures.length) || (showPickupLocations && allFeatures.length)) {
          const geoExtent = geojsonExtent({
            "type": "FeatureCollection",
            "features": allFeatures
          });
          if( !isEqual(geoExtentRef.current, geoExtent) ) {
            map.current.fitBounds(geoExtent, { padding: 40, animate });
            geoExtentRef.current = geoExtent;
            if(!animate) {
              setAnimate(true);
            }
          }
        }
        resizeMap();
      },
      [
        showDeliveryAreas,
        kitchenInZone,
        destCoordinates,
        deliveryAreas,
        showPickupLocations,
        pickupLocations,
        resizeMap,
        map,
        driverCoordinates,
        showDriverMarker,
        showDestMarker,
        location,
        animate,
        setAnimate,
        geoExtentRef,
        showPlaces,
        currentPlace,
        deliveryAreasRef,
      ]
    );

    const onMapLoad = useCallback(
      () => {
        const mapCenter  = (showDestMarker && destCoordinates.length) ?
          destCoordinates : defaultCoordinates;
        const selectedDropoffCoord = getSelectedDropoffCoordinates(currentPlace, selectedDropoff);
        const hasDropoff = showPlaces && selectedDropoffCoord;

        if (showNavControls) {
          map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-left');
        }

        if(!isEmpty(mapCenter)) {
          map.current.setCenter(mapCenter);
        }

        if (hasDropoff) {
          destMarkerRef.current = document.createElement('div');
          destMarkerRef.current.className = `mapbox-marker ${destinationMarkerClassName}`;
          destMarker.current = new mapboxgl.Marker(destMarkerRef.current)
            .setLngLat(selectedDropoffCoord)
            .addTo(map.current);
      }

        if (!hasDropoff && showDestMarker && destCoordinates.length) {
          destMarkerRef.current = document.createElement('div');
          destMarkerRef.current.className = `mapbox-marker ${destinationMarkerClassName}`;
          destMarker.current = new mapboxgl.Marker(destMarkerRef.current)
            .setLngLat(destCoordinates)
            .addTo(map.current);
        }

        if (showDriverMarker && driverCoordinates.length) {
          driverMarkerRef.current = document.createElement('div');
          driverMarkerRef.current.className = 'mapbox-marker mapbox-driver-marker';
          driverMarker.current = new mapboxgl.Marker(driverMarkerRef.current)
            .setLngLat(driverCoordinates)
            .addTo(map.current);
        }

        if (!isEmpty(pickupLocations)) {
          each(pickupLocations, (pickupLocation, i) => {
            const elem = document.createElement('div')
            elem.className = `mapbox-marker mapbox-pickup-marker ${showPickupLocations ? '' : 'hidden'} ${currentPickupLocationId === pickupLocation.id ? 'selected' : ''}`
            elem.data_id = pickupLocation.id
            elem.innerHTML = '&nbsp;'
            if (onPickupLocationClicked) {
              elem.onclick = onPickupLocationClicked.bind(this, pickupLocation.id)
            }
            const coordinates = convertPointToGEOJSONFormat([get(pickupLocation, 'pickup_details.location', {})])
            const marker = new mapboxgl.Marker(elem)
              .setLngLat(coordinates)
              .addTo(map.current)
            pickupLocationsRef.current[pickupLocation.id] = {
              elem,
              marker
            }
          })
        }

        setMapReady(true);
        if (!kitchenInZone && showDeliveryAreas) {
          fitMapToBounds();
        }
      },
      [
        destCoordinates,
        map,
        destMarker,
        fitMapToBounds,
        showDriverMarker,
        driverMarkerRef,
        driverCoordinates,
        defaultCoordinates,
        showNavControls,
        showDestMarker,
        kitchenInZone,
        showDeliveryAreas,
        destinationMarkerClassName,
        setMapReady,
        selectedDropoff,
        showPlaces,
        currentPlace,
        showPickupLocations,
        pickupLocations,
        pickupLocationsRef,
        onPickupLocationClicked,
        currentPickupLocationId,
      ]
    );

    const initMap = useCallback(
      () => {
        map.current = new mapboxgl.Map({
          container: mapRef.current,
          center: DEFAULT_CENTER,
          zoom,
          maxZoom: 17,
          style: mapStyle,
          interactive: showNavControls
        });
        map.current.on('load', () => setMapLoaded(true));
        map.current.on('style.load', () => setStyleLoaded(true))
      },
      [
        zoom,
        mapStyle,
        map,
        showNavControls,
        setMapLoaded,
        setStyleLoaded,
      ]
    );

    const refreshMap = useCallback(
      () => {
        const hasDest = showDestMarker && destCoordinates.length;
        const hasDriver = showDriverMarker && driverCoordinates.length;
        const selectedDropoffCoord = getSelectedDropoffCoordinates(currentPlace, selectedDropoff);
        const hasDropoff = showPlaces && selectedDropoffCoord && selectedDropoffCoord.length;

        if (hasDropoff) {
          if (destMarker.current) {
            destMarker.current.setLngLat(selectedDropoffCoord);
          } else {
            destMarkerRef.current = document.createElement('div');
            destMarkerRef.current.className = `mapbox-marker ${destinationMarkerClassName}`;
            destMarker.current = new mapboxgl.Marker(destMarkerRef.current)
              .setLngLat(selectedDropoffCoord)
              .addTo(map.current);
          }
        }

        if (!hasDropoff && hasDest) {
          if (!isEqual(destCoordinates, renderedDestCoordinates)) {
            map.current.setCenter(destCoordinates);
          }
          if (destMarker.current) {
            destMarker.current.setLngLat(destCoordinates);
          } else {
            destMarkerRef.current = document.createElement('div');
            destMarkerRef.current.className = `mapbox-marker ${destinationMarkerClassName}`;
            destMarker.current = new mapboxgl.Marker(destMarkerRef.current)
              .setLngLat(destCoordinates)
              .addTo(map.current);
          }
        }

        if (!hasDest && !hasDropoff) {
          if (destMarker.current) {
            destMarker.current.remove();
            destMarker.current = null;
          }
        }

        if (hasDriver) {
          if (driverMarker.current) {
            driverMarker.current.setLngLat(driverCoordinates);
          } else {
            driverMarkerRef.current = document.createElement('div');
            driverMarkerRef.current.className = 'mapbox-marker mapbox-driver-marker';
            driverMarker.current = new mapboxgl.Marker(driverMarkerRef.current)
              .setLngLat(driverCoordinates)
              .addTo(map.current);
          }
        } else {
          if (driverMarker.current) {
            driverMarker.current.remove();
            driverMarker.current = null;
          }
        }

        each(pickupLocationsRef.current, ({elem, marker}) => {
          elem.className = `mapbox-marker mapbox-pickup-marker ${showPickupLocations ? '' : 'hidden'} ${currentPickupLocationId === elem.data_id ? 'selected' : ''}`
        })

        fitMapToBounds();
      },
      [
        destCoordinates,
        renderedDestCoordinates,
        map,
        destMarker,
        fitMapToBounds,
        showDriverMarker,
        driverMarker,
        driverCoordinates,
        showDestMarker,
        destinationMarkerClassName,
        selectedDropoff,
        showPlaces,
        currentPlace,
        showPickupLocations,
        currentPickupLocationId,
      ]
    );

    useEffect(
      () => {
        if (mapLoaded && styleLoaded && !mapReady) {
          onMapLoad()
        }
      },
      [
        mapLoaded,
        styleLoaded,
        mapReady,
        onMapLoad,
      ]
    );

    useEffect(
      () => {
        if (!initialized) {
          if (!mapboxgl.supported()) {
            setMapUnsupported(true);
          } else {
            mapboxgl.accessToken = window.ENV.MAPBOX_API_ACCESS_TOKEN;
            initMap();
            setInitialized(true);
          }
        } else if (mapReady) {
          refreshMap();
        }
        if (!isEqual(renderedDestCoordinates, destCoordinates)){
          setRenderedDestCoordinates(destCoordinates)
        }
      },
      [
        initMap,
        initialized,
        refreshMap,
        mapReady,
        renderedDestCoordinates,
        destCoordinates,
        currentPlace
      ]
    );

    // only fire this useEffect onMount and onUnmount
    // array of dependencies is intentionally empty
    useEffect(
      () => () => {
        if (map.current) {
          map.current.remove();
        }
      },
      []
    );

    return (
      mapUnsupported ? <div
      className='mapbox-unsupported'
      style={{ width, height }}>
        <h3>
          Interactive map unavailable.
        </h3>
        <p>
          Please update your web browser to the latest version.
        </p>
      </div> :
      <div
        id="modal-delivery-map"
        style={{ width, height }}
        ref={mapRef} />
    );
  }

export default Mapbox;
